import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Motion helpers for the desktop redesign ("organic" motion).
 * Every animation is skipped (final value, no movement) under
 * `prefers-reduced-motion: reduce`.
 */

/**
 * Position (0 -> 1, may overshoot slightly) of an under-damped spring at
 * normalized time `t` (0..1). `damping` is the damping ratio (0 < d < 1),
 * `stiffness` the angular frequency scaled to the unit duration.
 */
export function springAt(t: number, damping = 0.72, stiffness = 7.5): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const dampedFreq = stiffness * Math.sqrt(1 - damping * damping);
  const decay = Math.exp(-damping * stiffness * t);
  return (
    1 -
    decay *
      (Math.cos(dampedFreq * t) +
        ((damping * stiffness) / dampedFreq) * Math.sin(dampedFreq * t))
  );
}

/** Builds a CSS `linear()` easing that samples `springAt`. */
export function springLinear(
  samples = 24,
  damping = 0.72,
  stiffness = 7.5,
): string {
  const points: string[] = [];
  for (let i = 0; i <= samples; i++) {
    const value = i === samples ? 1 : springAt(i / samples, damping, stiffness);
    points.push(String(Number(value.toFixed(4))));
  }
  return `linear(${points.join(", ")})`;
}

/**
 * Gentle spring with a ~4% overshoot (~500ms): springAt(t, 0.72, 7.5) sampled
 * 12 times. Mirrors `--desk-spring` in src/index.css (kept in sync by a test).
 */
export const SPRING_EASING =
  "linear(0, 0.143, 0.415, 0.672, 0.858, 0.969, 1.022, 1.038, 1.035, 1.025, 1.015, 1.007, 1)";
/**
 * Snappy, nearly critically damped spring (~300ms): springAt(t, 0.9, 9)
 * sampled 8 times. Mirrors `--desk-spring-snappy`.
 */
export const SPRING_SNAPPY_EASING =
  "linear(0, 0.326, 0.7, 0.897, 0.973, 0.997, 1.001, 1.001, 1)";
export const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
export const SPRING_DURATION_MS = 500;
export const SPRING_SNAPPY_DURATION_MS = 300;

/** Ease-out-expo, used by the KPI count-up. */
export function easeOutExpo(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - Math.pow(2, -10 * t);
}

/** Interpolated value between `from` and `to` at progress `t`, rounded to `decimals`. */
export function countUpValue(
  from: number,
  to: number,
  t: number,
  decimals = 0,
): number {
  const raw = from + (to - from) * easeOutExpo(t);
  const factor = Math.pow(10, decimals);
  return Math.round(raw * factor) / factor;
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const readReducedMotion = (): boolean =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(REDUCED_MOTION_QUERY).matches
    : false;

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReducedMotion);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(REDUCED_MOTION_QUERY);
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return reduced;
}

export interface CountUpOptions {
  duration?: number;
  decimals?: number;
  enabled?: boolean;
}

/**
 * Animates a number from its previous value (0 on mount) to `target` with
 * requestAnimationFrame. Returns `target` immediately under reduced motion or
 * when `enabled` is false.
 */
export function useCountUp(
  target: number,
  { duration = 900, decimals = 0, enabled = true }: CountUpOptions = {},
): number {
  const reduced = usePrefersReducedMotion();
  const animate = enabled && !reduced && Number.isFinite(target);
  const [value, setValue] = useState(animate ? 0 : target);
  const fromRef = useRef(animate ? 0 : target);

  useEffect(() => {
    if (!animate) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const from = fromRef.current;
    if (from === target) {
      setValue(target);
      return;
    }
    let frame = 0;
    let start: number | null = null;
    const step = (now: number) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      const next = countUpValue(from, target, t, decimals);
      fromRef.current = next;
      setValue(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, decimals, animate]);

  return value;
}

/**
 * True once the element has entered the viewport (never flips back). Used to
 * draw charts on first view. Falls back to `true` without IntersectionObserver.
 */
export function useInViewOnce<T extends Element>(
  ref: RefObject<T | null>,
  rootMargin = "0px 0px -10% 0px",
): boolean {
  const [inView, setInView] = useState(
    () => typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    if (inView) return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin, inView]);

  return inView;
}

/** Formats a figure with the locale's grouping (tabular-friendly). */
export function formatFigure(
  value: number,
  locale?: string,
  decimals = 0,
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
